import { computed } from 'vue';
import { useStore } from '@/store';
import {
  getTableQueryForCurrentTab,
  getTableQueryTemporaryForCurrentTab,
  getTableQuerySearch,
  getTableQueryTemporarySearch,
  getActiveReportIndex,
  getTableColumnsFiltersForCurrentTab,
  getTableColumnsFiltersForCurrentTabInQueryFormat,
  getTableSelectedItem,
  getTableSelectedItems,
  getTableExportParams,
  getTableCustomEvents,
  getTableDrilldownFilter,
  getTableDrilldown,
  getTablePreviewLink,
  getTableCustomGroups,
  getTableIsHideFilters,
  getTableMenuOverride,
  getTableNextReportGroup,
  getTableNotesOpened,
  getTableChartOpen,
  getTableTotal,
  getTableProgressBlockVisible,
  getTableNoReportBlockVisible,
  getTableActiveTab,
  getTableSelectedReport,
  getTableLastActiveTab,
  getTableEntityNames,
  getTableChartHiddenSeries,
  getTableChartHiddenSeriesDashboard,
  getTableQuery,
  getTableQueryTemporary,
  getTableActiveReports,
  patchTableQueryField,
  patchTableQueryTemporaryField,
  patchTableQuerySearchTemporary,
  applyTableQuerySearch,
  patchTableOffsetForCurrentTab,
  patchTablePeriodForCurrentTab,
  patchTablePeriodTemporaryForCurrentTab,
  setTableView,
  setTableViewTemporary,
  addTableColumnFilter,
  removeTableColumnFilter,
  setTableTreeLevelsForCurrentTab,
  setTableTreeLevelsTemporaryForCurrentTab,
  setTableTreeIndexForCurrentTab,
  resetTableTreeForCurrentTab,
  clearTableSelectedItems,
} from '@/store/modules/table';

export const useTableQuery = () => {
  const store = useStore();

  const tableQueryForCurrentTab = computed(() => {
    void store.table.activeTab;
    void store.table.activeReports;
    void store.table.query;
    return getTableQueryForCurrentTab();
  });

  const tableQueryTemporaryForCurrentTab = computed(() => {
    void store.table.activeTab;
    void store.table.activeReports;
    void store.table.queryTemporary;
    return getTableQueryTemporaryForCurrentTab();
  });

  const tableQuerySearch = computed(() => {
    void store.table.activeTab;
    void store.table.activeReports;
    void store.table.query;
    void store.user?.reportSearchSetting;
    return getTableQuerySearch();
  });

  const tableQueryTemporarySearch = computed(() => {
    void store.table.activeTab;
    void store.table.activeReports;
    void store.table.queryTemporary;
    void store.user?.reportSearchSetting;
    return getTableQueryTemporarySearch();
  });

  const tableColumnsFiltersForCurrentTab = computed(() => {
    void store.table.activeTab;
    void store.table.activeReports;
    void store.table.filters;
    return getTableColumnsFiltersForCurrentTab();
  });

  const activeReportIndex = computed(() => {
    void store.table.activeTab;
    void store.table.activeReports;
    return getActiveReportIndex();
  });

  const tableSelectedItem = computed(() => {
    void store.table.selectedItems;
    return getTableSelectedItem();
  });

  const tableSelectedItems = computed(() => {
    void store.table.selectedItems;
    return getTableSelectedItems();
  });

  const tableExportParams = computed(() => {
    void store.table.exportParams;
    return getTableExportParams();
  });

  const tableCustomEvents = computed(() => {
    void store.table.customEvents;
    return getTableCustomEvents();
  });

  const tableDrilldownFilter = computed(() => {
    void store.table.drilldownFilter;
    return getTableDrilldownFilter();
  });

  const tableDrilldown = computed(() => {
    void store.table.drilldown;
    return getTableDrilldown();
  });

  const tablePreviewLink = computed(() => {
    void store.table.previewLink;
    return getTablePreviewLink();
  });

  const tableCustomGroups = computed(() => {
    void store.table.customGroups;
    return getTableCustomGroups();
  });

  const tableIsHideFilters = computed(() => {
    void store.table.isHideFilters;
    return getTableIsHideFilters();
  });

  const tableMenuOverride = computed(() => {
    void store.table.menuOverride;
    return getTableMenuOverride();
  });

  const tableNextReportGroup = computed(() => {
    void store.table.nextReportGroup;
    return getTableNextReportGroup();
  });

  const tableNotesOpened = computed(() => {
    void store.table.notesOpened;
    return getTableNotesOpened();
  });

  const tableChartOpen = computed(() => {
    void store.table.chartOpen;
    return getTableChartOpen();
  });

  const tableTotal = computed(() => {
    void store.table.total;
    return getTableTotal();
  });

  const tableProgressBlockVisible = computed(() => {
    void store.table.progressBlockVisible;
    return getTableProgressBlockVisible();
  });

  const tableNoReportBlockVisible = computed(() => {
    void store.table.noReportBlockVisible;
    return getTableNoReportBlockVisible();
  });

  const tableActiveTab = computed(() => {
    void store.table.activeTab;
    return getTableActiveTab();
  });

  const tableQuery = computed(() => {
    void store.table.query;
    return getTableQuery();
  });

  const tableQueryTemporary = computed(() => {
    void store.table.queryTemporary;
    return getTableQueryTemporary();
  });

  const tableSelectedReport = computed(() => {
    void store.table.selectedReport;
    return getTableSelectedReport();
  });

  const tableLastActiveTab = computed(() => {
    void store.table.lastActiveTab;
    return getTableLastActiveTab();
  });

  const tableChartHiddenSeries = computed(() => {
    void store.table.chartHiddenSeries;
    return getTableChartHiddenSeries();
  });

  const tableChartHiddenSeriesDashboard = computed(() => {
    void store.table.chartHiddenSeriesDashboard;
    return getTableChartHiddenSeriesDashboard();
  });

  const tableActiveReports = computed(() => {
    void store.table.activeReports;
    return getTableActiveReports();
  });

  const tableEntityNames = computed(() => {
    void store.table.entityNames;
    return getTableEntityNames();
  });

  const tableSettingsTabRoute = computed(() => {
    void store.table.settingsTabRoute;
    return store.table.settingsTabRoute;
  });

  return {
    tableQueryForCurrentTab,
    tableQueryTemporaryForCurrentTab,
    tableQuerySearch,
    tableQueryTemporarySearch,
    tableColumnsFiltersForCurrentTab,
    activeReportIndex,
    tableSelectedItem,
    tableSelectedItems,
    tableExportParams,
    tableCustomEvents,
    tableDrilldownFilter,
    tableDrilldown,
    tablePreviewLink,
    tableCustomGroups,
    tableIsHideFilters,
    tableMenuOverride,
    tableNextReportGroup,
    tableNotesOpened,
    tableChartOpen,
    tableTotal,
    tableProgressBlockVisible,
    tableNoReportBlockVisible,
    tableActiveTab,
    tableQuery,
    tableQueryTemporary,
    tableSelectedReport,
    tableLastActiveTab,
    tableChartHiddenSeries,
    tableChartHiddenSeriesDashboard,
    tableActiveReports,
    tableEntityNames,
    tableSettingsTabRoute,
    getTableQueryForCurrentTab,
    getTableColumnsFiltersForCurrentTab,
    getTableColumnsFiltersForCurrentTabInQueryFormat,
    getTableQuerySearch,
    getTableSelectedItem,
    getTableSelectedItems,
    getTableExportParams,
    getTableCustomEvents,
    getTableDrilldownFilter,
    getTableDrilldown,
    getTablePreviewLink,
    getTableCustomGroups,
    getTableIsHideFilters,
    getTableMenuOverride,
    getTableNextReportGroup,
    getTableNotesOpened,
    getTableChartOpen,
    getTableTotal,
    getTableProgressBlockVisible,
    getTableNoReportBlockVisible,
    getTableActiveTab,
    getTableSelectedReport,
    getTableLastActiveTab,
    getTableEntityNames,
    getTableChartHiddenSeries,
    getTableChartHiddenSeriesDashboard,
    getTableQuery,
    getTableQueryTemporary,
    getTableActiveReports,
    patchTableQueryField,
    patchTableQueryTemporaryField,
    patchTableQuerySearchTemporary,
    applyTableQuerySearch,
    patchTableOffsetForCurrentTab,
    patchTablePeriodForCurrentTab,
    patchTablePeriodTemporaryForCurrentTab,
    setTableView,
    setTableViewTemporary,
    addTableColumnFilter,
    removeTableColumnFilter,
    setTableTreeLevelsForCurrentTab,
    setTableTreeLevelsTemporaryForCurrentTab,
    setTableTreeIndexForCurrentTab,
    resetTableTreeForCurrentTab,
    clearTableSelectedItems,
  };
};
